/**
 * Data layer for the strategy-builder chat (WP-19d).
 *
 * Server state (session list, one session + its history, the strategy catalog
 * for the "load existing" selector) lives in TanStack Query and flows through
 * the type-safe `hc<AppType>` client. The streaming turn itself is NOT here —
 * that runs over the AI SDK's `useChat` transport straight against
 * `POST /api/chat/sessions/:id/stream` (see `useBuilderChat`) because SSE is
 * outside Query's request/response model.
 *
 * The server keeps `chat_sessions.draft_dsl` as the source of truth (§6); the
 * client reconciles the right-panel document back to it via `PUT .../draft`
 * (patches the server does not fold) and promotes it with `POST .../save`.
 */
import { queryOptions } from "@tanstack/react-query";
import {
  dropNullish,
  type ChatSession,
  type ChatSessionDetailResponse,
  type ListChatSessionsResponse,
  type ListStrategiesResponse,
  type Strategy,
  type StrategyDsl,
} from "@richrich/contracts";
import { api, unwrap } from "../../lib/api";
import { keys } from "../../lib/queryKeys";

/** `GET /api/chat/sessions` — all builder sessions, most-recent first. */
export const chatSessionsQuery = queryOptions({
  queryKey: keys.chat.sessions(),
  queryFn: async (): Promise<ListChatSessionsResponse> =>
    unwrap<ListChatSessionsResponse>(await api.chat.sessions.$get()),
  staleTime: 15_000,
});

/** `GET /api/chat/sessions/:id` — one session plus its persisted message history. */
export function chatSessionQuery(id: string) {
  return queryOptions({
    queryKey: keys.chat.session(id),
    queryFn: async (): Promise<ChatSessionDetailResponse> =>
      unwrap<ChatSessionDetailResponse>(await api.chat.sessions[":id"].$get({ param: { id } })),
    staleTime: 5_000,
  });
}

/**
 * `GET /api/chat/sessions/:id?after=<ts>` — the lite-resume DELTA poll (§6).
 *
 * Returns the session meta + only the message rows with `ts >= after`, so a
 * steady-state poll re-fetches just the growing checkpoint row (constant ts,
 * hence `gte`) instead of the whole history. This deliberately BYPASSES the
 * TanStack cache: the response is a PARTIAL message list, so writing it under
 * `keys.chat.session(id)` would poison the shared session-detail entry the route
 * loader and `useQuery(chatSessionQuery)` read (they expect the full history).
 * The resume hook keeps its own authoritative row list and reconciles to a real
 * full fetch (through the cache) at turn end.
 *
 * The `/sessions/:id` route reads `after` straight off the query string (no zod
 * validator, so it stays ignore-and-return-full on garbage); the typed `hc`
 * client therefore doesn't surface `query` for this route, hence the cast — the
 * hono runtime still serializes it into the URL.
 */
export async function fetchChatSessionDelta(
  id: string,
  after: number,
): Promise<ChatSessionDetailResponse> {
  return unwrap<ChatSessionDetailResponse>(
    await api.chat.sessions[":id"].$get({
      param: { id },
      query: { after: String(after) },
    } as Parameters<(typeof api.chat.sessions)[":id"]["$get"]>[0]),
  );
}

/**
 * `GET /api/strategies` — backs the "refine an existing strategy" selector.
 * Shares `keys.strategies.list()` with the strategies feature's own unfiltered
 * read (previously a separate `['strategies','list']` key — see queryKeys.ts).
 */
export const strategiesListQuery = queryOptions({
  queryKey: keys.strategies.list(),
  queryFn: async (): Promise<ListStrategiesResponse> =>
    unwrap<ListStrategiesResponse>(await api.strategies.$get({ query: {} })),
  staleTime: 30_000,
});

/** `POST /api/chat/sessions` — start a session; `strategyId` seeds a refine loop. */
export async function createChatSession(input: {
  title?: string;
  strategyId?: string;
}): Promise<ChatSession> {
  return unwrap<ChatSession>(await api.chat.sessions.$post({ json: input }));
}

/** `DELETE /api/chat/sessions/:id` — remove a session and its message history. */
export async function deleteChatSession(id: string): Promise<void> {
  await unwrap<{ ok: boolean }>(await api.chat.sessions[":id"].$delete({ param: { id } }));
}

/** `PUT /api/chat/sessions/:id/draft` — reconcile the right-panel document. */
export async function putChatDraft(id: string, draftDsl: StrategyDsl): Promise<ChatSession> {
  return unwrap<ChatSession>(
    await api.chat.sessions[":id"].draft.$put({ param: { id }, json: { draftDsl } }),
  );
}

/** `POST /api/chat/sessions/:id/save` — promote the draft into a `strategies` row. */
export async function saveChatDraft(id: string, name?: string): Promise<Strategy> {
  const json = dropNullish({ name });
  return unwrap<Strategy>(await api.chat.sessions[":id"].save.$post({ param: { id }, json }));
}
